import {
    Box,
    Button,
    capitalize,
    Chip,
    Stack,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import {useCallback, useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router";
import {GridColDef} from "@mui/x-data-grid";
import {
    DataLoader,
    DataRequest,
    DataRequestState,
    useDataRequest,
} from "../data";
import {useT} from "../../i18n";
import SearchField from "../SearchField";
import {useGetApi} from "../../client";
import {EntsorgerInfo} from "../../api";
import CheckIcon from "@mui/icons-material/Check";
import DataTable from "../data/DataTable";
import useCustomFilters from "../data/useCustomFilters";

export type EntsorgerFilter = {
    search?: string;
    filtered?: string;
};

export type Props = Omit<DataRequest<EntsorgerFilter>, "filter"> &
    Partial<Pick<DataRequest<EntsorgerFilter>, "filter">>;

const tableName: string = "entsorger"

export default function EntsorgerDataTable({...input}: Props) {
    const request = useDataRequest<EntsorgerFilter>({filter: {}, ...input});
    const [filteredState, setFilteredState] = useState("");
    return (
        <Stack spacing={2}>
            <FilterComp
                request={request}
                filteredStateFilter={filteredState}
            />
            <EntsorgerResults request={request} setFilteredState={setFilteredState}/>
        </Stack>
    );
}

function FilterComp({
                        request,
                        filteredStateFilter,
                    }: {
    request: DataRequestState<EntsorgerFilter>;
    filteredStateFilter: string;
}) {
    const {setFilter} = request;
    const {currentCustomFilter, setCustomField} = useCustomFilters<EntsorgerFilter>({
        tableName: tableName,
        defaultFilter: {
            search: "",
        }
    });
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const {t: translation} = useT();
    useEffect(() => {
        setFilter({
            search: currentCustomFilter.search,
            filtered: filteredStateFilter || undefined,
        });
    }, [currentCustomFilter, filteredStateFilter, setFilter]);
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                position: "relative",
                width: "100%",
            }}
        >
            {isMobile ? (
                <Stack direction="row">
                    <SearchField
                        value={currentCustomFilter.search}
                        onChange={(val) => setCustomField("search", val)}
                    />
                    <Button
                        variant="contained"
                        color="secondary"
                        sx={{marginLeft: 2}}
                        onClick={() => {
                            navigate("/neu-entsorger");
                        }}
                    >
                        {capitalize(translation("create-new"))}
                    </Button>
                </Stack>
            ) : (
                <>
                    <SearchField
                        value={currentCustomFilter.search}
                        onChange={(val) => setCustomField("search", val)}
                    />
                    <Button
                        variant="contained"
                        color="secondary"
                        sx={{position: "absolute", top: 0, right: 0}}
                        onClick={() => {
                            navigate("/neu-entsorger");
                        }}
                    >
                        {capitalize(translation("create-new"))}
                    </Button>
                </>
            )}
        </Box>
    );
}

function EntsorgerResults({
                              request,
                              setFilteredState,
                          }: {
    request: DataRequestState<EntsorgerFilter>;
    setFilteredState: (value: string) => void;
}) {
    return <Results request={request} setFilteredState={setFilteredState}/>;
}

function Results({
                     request,
                     setFilteredState,
                 }: {
    request: DataRequestState<EntsorgerFilter>;
    setFilteredState: (value: string) => void;
}) {
    const navigate = useNavigate();
    const getApi = useGetApi();
    const loadEntsorger: DataLoader<EntsorgerFilter, EntsorgerInfo> = useCallback(
        async (params) => (await getApi()).entsorger.sucheEntsorger({...params}),
        [getApi]
    );
    return (
        <DataTable
            columns={useColumns()}
            request={request}
            queryKey={[tableName]}
            loadData={loadEntsorger}
            onRowClick={(row) => {
                navigate("/entsorger/" + row.id);
            }}
            name={tableName}
            setFiltered={setFilteredState}
        />
    );
}

function useColumns(): Array<GridColDef<EntsorgerInfo>> {
    const {t: translation} = useT("entsorger");

    return useMemo(
        () => [
            {
                field: "datum",
                headerName: capitalize(translation("date")),
                type: "date",
                valueGetter: (value) => {
                    return new Date(value);
                },
                renderCell: (params) => {
                    if (params.value) {
                        return new Date(params.value).toLocaleDateString(undefined, {
                            month: "2-digit",
                            day: "2-digit",
                            year: "numeric",
                        });
                    }
                    return "";
                },
            },
            {
                field: "verbleibortKurzname",
                headerName: capitalize(translation("entsorger-attributes-remain-location")),
                flex: 1,
            },
            {
                field: "nummer",
                headerName: capitalize(translation("en-number")),
                flex: 1,
            },
            {
                field: "materialienName",
                headerName: capitalize(translation("entsorger-attributes-waste-type")),
                flex: 1,
            },
            {
                field: "abfallartKey",
                headerName: capitalize(translation("entsorger-attributes-eak-key")),
                flex: 1,
            },
            {
                field: "archiviert",
                headerName: capitalize(translation("archived")),
                flex: 1,
                type: "boolean",
                renderCell: (params) => {
                    if (params.value === true) {
                        return (
                            <Chip
                                icon={<CheckIcon/>}
                                label={capitalize(translation("archived"))}
                                color="secondary"
                            />
                        );
                    } else {
                        return "";
                    }
                },
            },
        ],
        [translation]
    );
}
